import { useSnackbar } from "notistack";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { usePanelProjectContext } from "src/App/BIMDigitalTwin/PanelProjectProvider";
import { usePanelViewerContext } from "src/App/Context/PanelViewerProvider";
import Circle from "../../../../../assets/spriteIcons/circle.svg";
import Co2 from "../../../../../assets/spriteIcons/co2.svg";
import Sensor from "../../../../../assets/spriteIcons/sensor.svg";
import Thermometer from "../../../../../assets/spriteIcons/thermometer.svg";
import { RenderModel2 } from "../../../../api/APIClient";
import {
    fetchAssetInformationByDbId,
    fetchAssetInformationById,
    fetchAssetsForDbid,
    fetchDocumentsByTagApi,
    fetchIsoPanel,
    fetchPropertyPanel,
    fetchThreadsPanel,
} from "../../../../api/Fetchers";
import {
    DocumentsRightPanelModel,
    FetchAssetInformationByDbIdQuery,
    FetchAssetInformationByIdQuery,
    FullAssetInformation,
    RenderModel,
    RenderModelType,
} from "../../../../Models/AssetInformationModels";
import { ChartsModel } from "../../../../Models/SignalModels";
import { ProjectContext, ProjectModelContext } from "../../Context/ProjectContext";
import {
    colorHexToRgb,
    convertHexToRGB,
    ViewerMode,
} from "../ForgeViewer/BIMDigitalTwinViewer/Utils";
import { HeatmapGroup } from "../Heatmaps/HeatmapModels";

interface IObjTest {
    xcoord: number;
    ycoord: number;
    data: ChartsModel;
}
interface Selection {
    dbIdArray: number[];
    fragIdsArray: number[];
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    model: any;
    nodeArray: number[];
}

interface AggregateSelectionEvent {
    mouseButton: number | undefined;
    selections: Selection[];
}

const getFloorFromName = (name: string | undefined): string | null => {
    if (!name) return null;
    const floorPattern = /(\d+F-|RF-)/;
    const match = name.match(floorPattern);

    if (match) {
        if (match[1] === "RF-") {
            return "5";
        }
        return match[1].replace("F-", "");
    }
    return null;
};

const getAreaFromName = (name: string | undefined): string | null => {
    if (!name) return null;
    switch (name) {
        case "EW":
            return "east";
        case "WW":
            return "west";
        case "Atrium":
            return "atrium";
        default:
            return "";
    }
};

const useAsset = () => {
    const project = useContext(ProjectContext);
    const model = useContext(ProjectModelContext);
    const { enqueueSnackbar } = useSnackbar();
    const {
        setHoveredDeviceInfo,
        setRightPanelsData,
        setIsRightPanelOpen,
        setIsViewerLoading,
        setIsRenderingItems,
        setAssetSelection,
        setViewerMode,
        setDbidsToRender,
        setRenderModel,
        setAssetsWithSameDbid,
        setDbIdsToEdit,
        setView,
        setRenderModelTypes,
        setRightPanelButtonsLoading,
        setFloorPlanes,
        userId,
        setIsolatedAssets,
        areaVisualization,
        setAreaVisualization,
    } = usePanelProjectContext();

    const { t } = useTranslation("project");
    const colorRenderModel = (renderModelToPaint: RenderModel[]) => {
        return new Promise((resolve) => {
            renderModelToPaint?.forEach((element) => {
                const hexColor = element.style?.hexColor;
                if (!hexColor || hexColor === "clear") return;
                const rgbColor = colorHexToRgb(hexColor!);
                const covertedColor = [rgbColor.r * 255, rgbColor.g * 255, rgbColor.b * 255];
                // window.NOP_VIEWER.setDbIdsColorByHex(element.dbIds!, hexColor!);
                window.NOP_VIEWER.setElementsSpecularColorByColor(covertedColor, element.dbIds!);
            });
            resolve(true);
        });
    };

    async function PrintDeviceIcons(res: ChartsModel[], alternate: number) {
        window.NOP_VIEWER.getExtension("Autodesk.DataVisualization");
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const devices = res;

        const SensorStyleDefinitions = {
            sensor: {
                url: Sensor,
            },
            co2: {
                url: Co2,
            },
            temperature: {
                url: Thermometer,
            },
            default: {
                url: Circle,
            },
        };

        const dataVizExt = window.NOP_VIEWER.getExtension(
            "Autodesk.DataVisualization"
        ) as Autodesk.Extensions.DataVisualization;
        const DATAVIZEXTN = Autodesk.DataVisualization.Core;
        const viewableType = DATAVIZEXTN.ViewableType.SPRITE;
        const styleMap = {};

        dataVizExt.removeAllViewables();

        // // Create model-to-style map from style definitions.
        const viewableData = new DATAVIZEXTN.ViewableData();

        viewableData.spriteSize = 30;
        if (devices && devices.length > 0) {
            devices.forEach((device) => {
                if (device?.dbIds && device?.dbIds.length > 0) {
                    Object.entries(SensorStyleDefinitions).forEach(([type, styleDef]) => {
                        styleMap[type] = new DATAVIZEXTN.ViewableStyle(
                            viewableType,
                            new THREE.Color(convertHexToRGB(device?.icon?.color)),
                            styleDef.url
                        );
                    });

                    // eslint-disable-next-line
                    const style = styleMap[device?.icon?.type!] || styleMap["default"];
                    // eslint-disable-next-line
                    window.NOP_VIEWER.addFrags(device?.dbIds!);
                    const dbIdBox = window.NOP_VIEWER.getModifiedWorldBoundingBox(
                        // eslint-disable-next-line
                        device?.dbIds!
                    );
                    const dbIdCenter = dbIdBox.center();
                    const viewable = new DATAVIZEXTN.SpriteViewable(
                        dbIdCenter,
                        style,
                        device.dbIds![0] + alternate
                    );
                    viewableData.addViewable(viewable);
                }
            });

            let click = false;

            const onItemHovering = (event) => {
                if (window.NOP_VIEWER.getExtension("Autodesk.DataVisualization")) {
                    if (event.hovering) {
                        const deviceId = event.dbId;
                        const device = devices.find((elm) =>
                            elm?.dbIds?.includes(deviceId - alternate)
                        );
                        if (device) {
                            const deviceBox = window.NOP_VIEWER.getModifiedWorldBoundingBox(
                                device!.dbIds!
                            );
                            const deviceCenter = deviceBox?.center();
                            const mappedPosition =
                                window.NOP_VIEWER.impl.worldToClient(deviceCenter);

                            const objTest: IObjTest = {
                                xcoord: mappedPosition.x,
                                ycoord: mappedPosition.y - 10,
                                data: device,
                            };
                            setHoveredDeviceInfo(objTest);
                        }
                    } else if (click === false) {
                        setHoveredDeviceInfo(undefined);
                    }
                }
            };

            const onItemClick = (event) => {
                if (window.NOP_VIEWER.getExtension("Autodesk.DataVisualization")) {
                    const deviceId = event.dbId;
                    const device = devices.find((elm) =>
                        elm?.dbIds?.includes(deviceId - alternate)
                    );
                    if (device) {
                        const deviceBox = window.NOP_VIEWER.getModifiedWorldBoundingBox(
                            device.dbIds!
                        );
                        const deviceCenter = deviceBox?.center();
                        const mappedPosition = window.NOP_VIEWER.impl.worldToClient(deviceCenter);

                        const objTest: IObjTest = {
                            xcoord: mappedPosition.x,
                            ycoord: mappedPosition.y - 10,
                            data: device,
                        };
                        click = true;
                    }

                    if (deviceId === 0) {
                        setHoveredDeviceInfo(undefined);
                        click = false;
                    }
                }
            };

            await viewableData.finish();
            setIsViewerLoading(false);
            const DataVizCore = Autodesk.DataVisualization.Core;

            window.NOP_VIEWER?.removeEventListener(DataVizCore.MOUSE_CLICK, onItemClick);
            window.NOP_VIEWER?.removeEventListener(DataVizCore.MOUSE_CLICK, onItemHovering);
            window.NOP_VIEWER?.addEventListener(DataVizCore.MOUSE_CLICK, onItemClick);
            window.NOP_VIEWER?.addEventListener(DataVizCore.MOUSE_HOVERING, onItemHovering);

            dataVizExt.addViewables(viewableData);
        }
    }

    const renderHTMLTooltips = (renderInformation: RenderModel[]) => {
        if (renderInformation.every((d) => d.dbIds !== null && d.dbIds !== undefined)) {
            if (renderInformation.every((e) => e.type === RenderModelType.Parent)) {
                enqueueSnackbar(`${t("viewer.messages.showOnlyParents")}`, {
                    variant: "info",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                });
            }
            setIsRenderingItems(true);
            window.NOP_VIEWER.clearAllLabels();
            renderInformation?.forEach((e) => {
                window.NOP_VIEWER.addLabelHeatMap(
                    e.dbIds!,
                    e.tooltip,
                    e.style?.hexColor,
                    e.tooltip?.data,
                    true,
                    undefined
                );
                // if (
                //     notifications.filter((x) => x.titleParams === assetId)?.length >
                //     0
                // ) {
                //     window.NOP_VIEWER.addLabelHeatMap(
                //         e.dbIds!,
                //         {
                //             type: "chat",
                //             content: notifications
                //                 .filter((x) => x.titleParams === assetId)[0]
                //                 .ids.split(",")
                //                 .length.toString(),
                //         },
                //         "#FFB959",
                //         undefined,
                //         true
                //     );
                // }
            });
            if (window.NOP_VIEWER.showLabels) {
                window.NOP_VIEWER.showAllLabels();
            } else {
                window.NOP_VIEWER.hideAllLabels();
            }
        }
    };

    const getCuttingPlaneAtTop = (dbids: number[]) => {
        const viewer = window.NOP_VIEWER;
        const vModel = viewer.model;
        const { instanceTree } = vModel.getData();

        const bbox = new THREE.Box3();
        dbids.forEach((dbid) => {
            instanceTree.enumNodeFragments(dbid, function (fragId) {
                const fragList = vModel.getFragmentList();
                const box = new THREE.Box3();
                fragList.getWorldBounds(fragId, box);
                bbox.union(box);
            });
        });

        const topHeight = bbox.max.z;
        const bottomHeight = bbox.min.z;

        if (
            topHeight === -Infinity ||
            topHeight === Infinity ||
            bottomHeight === -Infinity ||
            bottomHeight === Infinity
        ) {
            return;
        }

        const topPlane = new THREE.Vector4(0, 0, 1, -topHeight);
        const bottomPlane = new THREE.Vector4(0, 0, -1, bottomHeight);

        viewer.setCutPlanes([topPlane, bottomPlane]);
        setFloorPlanes([topPlane, bottomPlane]);

        viewer.impl.invalidate(true, true, true);
    };

    const renderModelViewer = (renderInformation: RenderModel[], showHtmlTooltips: boolean) => {
        setIsRenderingItems(true);
        window.NOP_VIEWER.mode = ViewerMode.AssetInfo;
        setViewerMode(window.NOP_VIEWER.mode);
        window.NOP_VIEWER.clearThemingAndSpecularColor();
        window.NOP_VIEWER.setOpacityModel(1, undefined);

        const dbidsToRender = renderInformation.map((element) => {
            return element.dbIds;
        });

        const fullArrayOfDbidToRender: (number | undefined | null)[] = dbidsToRender.flat();

        // window.NOP_VIEWER.isolate(fullArrayOfDbidToRender as number[]);
        // window.NOP_VIEWER.setOpacityModel(
        //     window.NOP_VIEWER.viewingMode.backgroundOpacity,
        //     window.NOP_VIEWER.viewingMode.backgroundColor
        // );

        if (window.NOP_VIEWER.isolated) {
            window.NOP_VIEWER.isolate(fullArrayOfDbidToRender as number[]);
        }

        getCuttingPlaneAtTop(fullArrayOfDbidToRender as number[]);
        window.NOP_VIEWER.fitToView(fullArrayOfDbidToRender as number[]);
        window.NOP_VIEWER.setLightPreset(window.NOP_VIEWER.viewingMode.light);
        window.NOP_VIEWER.setEnvMapBackground(false);

        // window.NOP_VIEWER.setElementsSpecularOriginalColor(fullArrayOfDbidToRender as number[]);

        if (window.NOP_VIEWER.viewingMode.colored) {
            colorRenderModel(renderInformation);
        }

        // Html tooltips
        if (showHtmlTooltips) {
            renderHTMLTooltips(renderInformation);
        }
        window.NOP_VIEWER.dbidsToRender = fullArrayOfDbidToRender as number[];
        setDbidsToRender(fullArrayOfDbidToRender as number[]);
    };

    const manageAssetInformation = (assetInfo: FullAssetInformation) => {
        const { render, rightPanels, index } = assetInfo;
        setRenderModel((render as RenderModel2[]) ?? null);

        if (index) {
            setAssetSelection(index);
        }
        setIsRenderingItems(false);

        // Render on Model
        const renderInfo: RenderModel[] | undefined | null = render;

        // if (window.NOP_VIEWER.viewingMode.colored) {
        //     colorRenderModel(renderInfo);
        // }

        if (
            renderInfo &&
            renderInfo.length > 0 &&
            renderInfo.some((e) => e.dbIds && e.dbIds?.length > 0)
        ) {
            const newRenderModelTypes: RenderModelType[] = [];

            const types = [
                RenderModelType.Children,
                RenderModelType.Parent,
                RenderModelType.Selected,
                RenderModelType.Noozle,
            ];

            types.forEach((type) => {
                if (renderInfo.some((e) => e.type === type)) {
                    newRenderModelTypes.push(type);
                }
            });
            setRenderModelTypes(newRenderModelTypes);

            renderModelViewer(renderInfo, true);

            // // Signal icons and tooltips
            // if (rightPanels?.signals?.data && rightPanels?.signals?.data.length > 0) {
            //     PrintDeviceIcons(rightPanels?.signals?.data, 0);

            //     /* ilboc 04/05/2023 */
            //     // PrintAlarmIcons(rightPanels?.signals?.data, 1);
            // } else {
            //     setIsViewerLoading(false);
            //     window.NOP_VIEWER.deleteDeviceIcons();
            // }
            setIsViewerLoading(false);
        } else {
            enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                variant: "warning",
                anchorOrigin: {
                    horizontal: "right",
                    vertical: "bottom",
                },
            });
            setIsViewerLoading(false);
        }
        // Right panels
        if (rightPanels) {
            // setRightPanelsData(rightPanels);
            setRightPanelsData((prevState) => ({
                ...prevState,
                ...rightPanels,
            }));
            setIsRightPanelOpen(true);
        }
    };

    const fetchAssetInfoOnlyRender = (projectId: string, assetId: string) => {
        fetchAssetInformationById({ projectId, id: assetId, userId: `${userId}` }).then((res) => {
            if (res.status === "success" && res.data.render) {
                const dbidsToRender = res.data.render.map((element) => {
                    return element.dbIds;
                });

                const fullArrayOfDbidToRender: (number | undefined | null)[] = dbidsToRender.flat();

                if (window.NOP_VIEWER.isolated) {
                    window.NOP_VIEWER.isolate(fullArrayOfDbidToRender as number[]);
                }

                getCuttingPlaneAtTop(fullArrayOfDbidToRender as number[]);
                window.NOP_VIEWER.fitToView(fullArrayOfDbidToRender as number[]);
                window.NOP_VIEWER.setLightPreset(window.NOP_VIEWER.viewingMode.light);
                window.NOP_VIEWER.setEnvMapBackground(false);
            }
        });
    };

    const fetchingAssetInformationById = (
        projectId: string,
        assetId: string,
        renderViewer = true,
        assetTag?: string | undefined
    ) => {
        setRightPanelsData(undefined);
        setIsViewerLoading(true);
        setRightPanelButtonsLoading(true);
        setIsRightPanelOpen(false);
        setDbidsToRender([]);
        const getAssetInformationByIdQuery: FetchAssetInformationByIdQuery = {
            projectId,
            id: assetId,
            userId: `${userId}`,
        };

        // promesa que se resuelve siempre ofreciendo el panel de documentos (con o sin info)
        const fetchDocumentsPromise = new Promise<DocumentsRightPanelModel>((resolve, reject) => {
            if (assetTag) {
                fetchDocumentsByTagApi(project.id, assetTag)
                    .then((res) => {
                        const modifiedRes = {
                            icon: "PictureAsPdf",
                            title: "Documents",
                            description: null,
                            data: res.data,
                        };
                        resolve(modifiedRes);
                    })
                    .catch((err) => {
                        console.error(err);
                        resolve({
                            icon: "PictureAsPdf",
                            title: "Documents",
                            description: null,
                            data: null,
                        });
                    });
            } else {
                const modifiedRes = {
                    icon: "PictureAsPdf",
                    title: "Documents",
                    description: null,
                    data: null,
                };
                resolve(modifiedRes);
            }
        });

        fetchAssetInformationById(getAssetInformationByIdQuery)
            .then((res) => {
                if (res.status === "success") {
                    window.NOP_VIEWER.resetModel(false);
                    window.NOP_VIEWER.dbidsToRender = [];
                    manageAssetInformation(res.data);
                    window.NOP_VIEWER.mode = ViewerMode.AssetInfo;
                    setViewerMode(window.NOP_VIEWER.mode);
                }
                return res.data;
            })
            .then((data) => {
                const rightPanelInfoPromises = [
                    fetchPropertyPanel(getAssetInformationByIdQuery).then((res) => {
                        if (res.status === "success") {
                            setRightPanelsData((prevState) => ({
                                ...prevState,
                                info: res.data,
                            }));
                        }
                    }),
                    fetchThreadsPanel(getAssetInformationByIdQuery).then((res) => {
                        if (res.status === "success") {
                            setRightPanelsData((prevState) => ({
                                ...prevState,
                                chat: res.data,
                            }));
                        }
                    }),
                    fetchDocumentsPromise.then((res) => {
                        if (res) {
                            setRightPanelsData((prevState) => ({
                                ...prevState,
                                documents: res,
                            }));
                        }
                    }),
                ];
                // add isometrics panel if it is enabled
                if (data.rightPanels?.isometrics) {
                    rightPanelInfoPromises.push(
                        fetchIsoPanel(getAssetInformationByIdQuery).then((res) => {
                            if (res.status === "success") {
                                setRightPanelsData((prevState) => ({
                                    ...prevState,
                                    isometrics: res.data,
                                }));
                            }
                        })
                    );
                }

                Promise.allSettled(rightPanelInfoPromises).then(() => {
                    setRightPanelButtonsLoading(false);
                });
            })
            .catch((err) => {
                console.error(err);
                enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                    variant: "warning",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                });
                setIsViewerLoading(false);
            });
    };

    const newFetchingAssetInformationById = (
        projectId: string,
        assetId: string,
        renderViewer = true,
        assetTag?: string | undefined
    ) => {
        setRightPanelsData(undefined);
        setIsViewerLoading(true);
        setRightPanelButtonsLoading(true);
        setIsRightPanelOpen(false);
        setDbidsToRender([]);
        const getAssetInformationByIdQuery: FetchAssetInformationByIdQuery = {
            projectId,
            id: assetId,
            userId: `${userId}`,
        };

        // promesa que se resuelve siempre ofreciendo el panel de documentos (con o sin info)
        const fetchDocumentsPromise = new Promise<DocumentsRightPanelModel>((resolve, reject) => {
            if (assetTag) {
                fetchDocumentsByTagApi(project.id, assetTag)
                    .then((res) => {
                        const modifiedRes = {
                            icon: "PictureAsPdf",
                            title: "Documents",
                            description: null,
                            data: res.data,
                        };
                        resolve(modifiedRes);
                    })
                    .catch((err) => {
                        console.error(err);
                        resolve({
                            icon: "PictureAsPdf",
                            title: "Documents",
                            description: null,
                            data: null,
                        });
                    });
            } else {
                const modifiedRes = {
                    icon: "PictureAsPdf",
                    title: "Documents",
                    description: null,
                    data: null,
                };
                resolve(modifiedRes);
            }
        });

        fetchAssetInformationById(getAssetInformationByIdQuery)
            .then((res) => {
                if (res.status === "success") {
                    window.NOP_VIEWER.resetModel(false);
                    window.NOP_VIEWER.dbidsToRender = [];
                    manageAssetInformation(res.data);
                    window.NOP_VIEWER.mode = ViewerMode.AssetInfo;
                    setViewerMode(window.NOP_VIEWER.mode);
                }

                const { render, index } = res.data;
                if (render) {
                    const allDbids = render.map((item) => item.dbIds).flat();
                    const cleanDbids = allDbids.filter(
                        (item) => item !== undefined && item !== null
                    );
                    console.log(cleanDbids);
                    setIsolatedAssets(cleanDbids as number[]);
                }
                if (index) {
                    setAreaVisualization({
                        ...areaVisualization,
                        level: getFloorFromName(index.tag) || "",
                        area: getAreaFromName(index.wing) || "",
                    });
                }

                return res.data;
            })
            .then((data) => {
                const rightPanelInfoPromises = [
                    fetchPropertyPanel(getAssetInformationByIdQuery).then((res) => {
                        if (res.status === "success") {
                            setRightPanelsData((prevState) => ({
                                ...prevState,
                                info: res.data,
                            }));
                        }
                    }),
                    fetchThreadsPanel(getAssetInformationByIdQuery).then((res) => {
                        if (res.status === "success") {
                            setRightPanelsData((prevState) => ({
                                ...prevState,
                                chat: res.data,
                            }));
                        }
                    }),
                    fetchDocumentsPromise.then((res) => {
                        if (res) {
                            setRightPanelsData((prevState) => ({
                                ...prevState,
                                documents: res,
                            }));
                        }
                    }),
                ];
                // add isometrics panel if it is enabled
                if (data.rightPanels?.isometrics) {
                    rightPanelInfoPromises.push(
                        fetchIsoPanel(getAssetInformationByIdQuery).then((res) => {
                            if (res.status === "success") {
                                setRightPanelsData((prevState) => ({
                                    ...prevState,
                                    isometrics: res.data,
                                }));
                            }
                        })
                    );
                }

                Promise.allSettled(rightPanelInfoPromises).then(() => {
                    setRightPanelButtonsLoading(false);
                });
            })
            .catch((err) => {
                console.error(err);
                enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                    variant: "warning",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                });
                setIsViewerLoading(false);
            });
    };

    const fetchingAssetInformation = (
        assetId: string | undefined,
        assetDbId: number | undefined,
        assetTag: string | undefined
    ) => {
        setIsRightPanelOpen(false);
        setView("");
        if (assetId) {
            fetchingAssetInformationById(project.id, assetId, true, assetTag);
        }
        if (assetDbId && assetDbId !== -1) {
            setIsViewerLoading(true);
            const getAssetInformationByDbIdQuery: FetchAssetInformationByDbIdQuery = {
                projectId: project.id,
                input: { projectId: project.id, dbId: assetDbId, userId: `${userId}` },
            };

            fetchAssetInformationByDbId(getAssetInformationByDbIdQuery)
                .then((res) => {
                    if (res.status === "success") {
                        window.NOP_VIEWER.resetModel(false);
                        window.NOP_VIEWER.dbidsToRender = [];
                        manageAssetInformation(res.data);
                        window.NOP_VIEWER.mode = ViewerMode.AssetInfo;
                        setViewerMode(window.NOP_VIEWER.mode);
                    }
                })
                .catch((err) => {
                    enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                        variant: "warning",
                        anchorOrigin: {
                            horizontal: "right",
                            vertical: "bottom",
                        },
                    });
                    // window.NOP_VIEWER.mode = ViewerMode.Default;
                    // setViewerMode(window.NOP_VIEWER.mode);
                    setIsViewerLoading(false);
                    // setIsRenderingItems(false);
                });
        }
    };

    const fetchingAssetInformationByDbId = (dbId: number | undefined, needRender = true) => {
        if (dbId) {
            setIsViewerLoading(true);
            setIsRightPanelOpen(false);
            setDbidsToRender([]);
            setView("");
            fetchAssetsForDbid(project.id, dbId)
                .then((res) => {
                    if (
                        res.status === "success" &&
                        res.data.assets &&
                        res.data.assets?.length > 0
                    ) {
                        if (
                            res.data.assets?.length === 1 &&
                            res.data.assets[0].id &&
                            res.data.assets[0].children!.length <= 0
                        ) {
                            newFetchingAssetInformationById(
                                project.id,
                                res.data.assets[0].id,
                                needRender,
                                res.data.assets[0].tag
                            );
                        } else if (res.data.assets[0].children!.length >= 0) {
                            setIsViewerLoading(false);
                            setAssetsWithSameDbid(res.data.assets);
                        }
                    }
                })
                .catch((err) => {
                    console.error(err);
                    enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                        variant: "warning",
                        anchorOrigin: {
                            horizontal: "right",
                            vertical: "bottom",
                        },
                    });
                    setIsViewerLoading(false);
                });
        }
    };

    const showEditAssetForm = (dbIdArr: number[] | undefined | never[]) => {
        if (dbIdArr && dbIdArr[0]) {
            setDbIdsToEdit(dbIdArr);
        } else {
            setDbIdsToEdit(undefined);
        }
    };

    const handleModelClick = (event: AggregateSelectionEvent) => {
        if (event.selections.length <= 0 || event.selections[0].model.myData.urn !== model.urn)
            return;
        if (window.NOP_VIEWER.mode === ViewerMode.EditAsset) {
            showEditAssetForm(event.selections[0].dbIdArray);
            window.NOP_VIEWER.setSelectionColor(
                new THREE.Color(0xedb100),
                Autodesk.Viewing.SelectionMode.REGULAR
            );

            window.NOP_VIEWER.setCanvasClickBehavior({
                click: {
                    onObject: ["selectToggle"],
                    offObject: ["none"],
                },
            });
        } else if (
            (window.NOP_VIEWER.mode === ViewerMode.Default ||
                window.NOP_VIEWER.mode === ViewerMode.AssetInfo) &&
            window.NOP_VIEWER.coordinatesMode === false
        ) {
            fetchingAssetInformationByDbId(event.selections[0].dbIdArray[0]);

            window.NOP_VIEWER.setCanvasClickBehavior({
                click: {
                    onObject: ["selectOnly"],
                    offObject: ["deselectAll"],
                },
            });
        }
    };

    return {
        fetchingAssetInformation,
        handleModelClick,
        colorRenderModel,
        fetchingAssetInformationById,
        newFetchingAssetInformationById,
        renderModelViewer,
        renderHTMLTooltips,
        fetchAssetInfoOnlyRender,
    };
};

export default useAsset;
